'use client';

/**
 * PIA/DPIA Generator
 * Privacy Impact Assessment / Data Protection Impact Assessment
 */

import { useState, useEffect, useCallback } from 'react';
import ToolSEO from '@/components/ToolSEO';
import {
  Assessment,
  AssessmentType,
  IdentifiedRisk,
  ProcessingPurpose,
  DataFlow,
  RiskLevel,
  ProcessingLawfulBasis,
  DataSubjectCategory,
  PersonalDataType,
  ASSESSMENT_TYPE_CONFIG,
  LAWFUL_BASIS_CONFIG,
  RISK_LEVEL_CONFIG,
  DATA_SUBJECT_CONFIG,
  PERSONAL_DATA_TYPE_CONFIG,
  generateAssessmentId,
  generateRiskId,
  generatePurposeId,
  generateDataFlowId,
  calculateOverallRisk,
} from '@/lib/pia-dpia/types';
import {
  saveAssessmentToStorage,
  loadAssessmentFromStorage,
  listAssessmentsFromStorage,
  deleteAssessmentFromStorage,
} from '@/lib/pia-dpia/storage';

// Tier access levels
const TIER_HIERARCHY = { free: 0, pro: 1, team: 2 };
type UserTier = 'free' | 'pro' | 'team';

function getFeatureAccess(tier: UserTier) {
  const tierLevel = TIER_HIERARCHY[tier];
  return {
    maxAssessments: tier === 'free' ? 2 : tier === 'pro' ? 10 : Infinity,
    canExport: tierLevel >= TIER_HIERARCHY.free,
    canExportWord: tierLevel >= TIER_HIERARCHY.pro,
  };
}

function createDefaultAssessment(type: AssessmentType): Assessment {
  const now = new Date().toISOString();
  return {
    id: generateAssessmentId(),
    type,
    name: `New ${ASSESSMENT_TYPE_CONFIG[type].label}`,
    projectDescription: '',
    status: 'draft',
    processingDescription: '',
    purposes: [],
    dataSubjects: [],
    personalDataTypes: [],
    dataFlows: [],
    thirdPartyProcessors: [],
    internationalTransfers: false,
    necessityJustification: '',
    proportionalityJustification: '',
    risks: [],
    dpoConsulted: false,
    dataSubjectsConsulted: false,
    supervisoryAuthorityConsultation: false,
    preparedBy: '',
    preparedDate: new Date().toISOString().split('T')[0],
    created_at: now,
    updated_at: now,
  };
}

type EditorSection =
  | 'overview'
  | 'data_flows'
  | 'necessity'
  | 'risks'
  | 'consultation'
  | 'review';

export default function PiaDpiaPage() {
  // State
  const [userTier] = useState<UserTier>('pro');
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [currentAssessment, setCurrentAssessment] = useState<Assessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editor states
  const [activeSection, setActiveSection] = useState<EditorSection>('overview');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [selectedType, setSelectedType] = useState<AssessmentType>('dpia');

  const features = getFeatureAccess(userTier);

  // Load assessments
  const loadAssessments = useCallback(async () => {
    try {
      setLoading(true);
      const list = await listAssessmentsFromStorage();
      setAssessments(list);
    } catch (err) {
      console.error('[PIA/DPIA] Failed to load:', err);
      setError('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssessments();
  }, [loadAssessments]);

  // Auto-save
  useEffect(() => {
    if (!currentAssessment || !hasChanges) return;

    const timer = setTimeout(async () => {
      try {
        setSaving(true);
        const updated = { ...currentAssessment, updated_at: new Date().toISOString() };
        await saveAssessmentToStorage(updated);
        setCurrentAssessment(updated);
        setHasChanges(false);
        setAssessments((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      } catch (err) {
        console.error('[PIA/DPIA] Save failed:', err);
      } finally {
        setSaving(false);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [currentAssessment, hasChanges]);

  // Handlers
  const handleCreateAssessment = async () => {
    if (assessments.length >= features.maxAssessments) {
      setError(`Limited to ${features.maxAssessments} assessments. Upgrade for more.`);
      return;
    }

    const newAssessment = createDefaultAssessment(selectedType);
    await saveAssessmentToStorage(newAssessment);
    setAssessments((prev) => [newAssessment, ...prev]);
    setCurrentAssessment(newAssessment);
    setShowNewDialog(false);
  };

  const handleOpenAssessment = async (id: string) => {
    try {
      const assessment = await loadAssessmentFromStorage(id);
      if (assessment) {
        setCurrentAssessment(assessment);
        setHasChanges(false);
        setActiveSection('overview');
      }
    } catch (err) {
      console.error('[PIA/DPIA] Failed to open:', err);
      setError('Failed to open assessment');
    }
  };

  const handleDeleteAssessment = async (id: string) => {
    if (!confirm('Delete this assessment?')) return;
    await deleteAssessmentFromStorage(id);
    setAssessments((prev) => prev.filter((a) => a.id !== id));
    if (currentAssessment?.id === id) {
      setCurrentAssessment(null);
    }
  };

  const updateAssessment = (updates: Partial<Assessment>) => {
    if (!currentAssessment) return;
    setCurrentAssessment({ ...currentAssessment, ...updates });
    setHasChanges(true);
  };

  const handleExportMarkdown = () => {
    if (!currentAssessment) return;

    const a = currentAssessment;
    let md = `# ${a.name}\n\n`;
    md += `**Type:** ${ASSESSMENT_TYPE_CONFIG[a.type].fullName}\n`;
    md += `**Status:** ${a.status}\n`;
    md += `**Prepared by:** ${a.preparedBy || 'Not specified'}\n`;
    md += `**Date:** ${a.preparedDate}\n\n`;
    md += `---\n\n`;

    md += `## 1. Processing Overview\n\n`;
    md += `### Project Description\n${a.projectDescription || 'Not provided'}\n\n`;
    md += `### Processing Description\n${a.processingDescription || 'Not provided'}\n\n`;

    if (a.purposes.length > 0) {
      md += `### Processing Purposes\n`;
      a.purposes.forEach((p, i) => {
        md += `${i + 1}. **${p.description}**\n`;
        md += `   - Lawful Basis: ${LAWFUL_BASIS_CONFIG[p.lawfulBasis].label}\n`;
        if (p.lawfulBasisJustification) md += `   - Justification: ${p.lawfulBasisJustification}\n`;
      });
      md += '\n';
    }

    if (a.dataSubjects.length > 0) {
      md += `### Data Subjects\n`;
      a.dataSubjects.forEach((ds) => md += `- ${DATA_SUBJECT_CONFIG[ds].label}\n`);
      md += '\n';
    }

    if (a.personalDataTypes.length > 0) {
      md += `### Personal Data Types\n`;
      const specialCats = a.personalDataTypes.filter((t) => PERSONAL_DATA_TYPE_CONFIG[t].isSpecialCategory);
      const normalCats = a.personalDataTypes.filter((t) => !PERSONAL_DATA_TYPE_CONFIG[t].isSpecialCategory);

      if (normalCats.length) {
        normalCats.forEach((t) => md += `- ${PERSONAL_DATA_TYPE_CONFIG[t].label}\n`);
      }
      if (specialCats.length) {
        md += `\n**Special Category Data:**\n`;
        specialCats.forEach((t) => md += `- ⚠️ ${PERSONAL_DATA_TYPE_CONFIG[t].label}\n`);
      }
      md += '\n';
    }

    md += `## 2. Data Flows\n\n`;
    if (a.dataFlows.length > 0) {
      a.dataFlows.forEach((f, i) => {
        md += `${i + 1}. **${f.source}** → **${f.destination}**\n`;
        md += `   - Data: ${f.dataTypes.map((t) => PERSONAL_DATA_TYPE_CONFIG[t].label).join(', ')}\n`;
        if (f.safeguards) md += `   - Safeguards: ${f.safeguards}\n`;
      });
    } else {
      md += 'No data flows documented.\n';
    }
    md += '\n';

    if (a.internationalTransfers) {
      md += `### International Transfers\n`;
      md += `Countries: ${a.transferCountries?.join(', ') || 'Not specified'}\n\n`;
    }

    md += `## 3. Necessity & Proportionality\n\n`;
    md += `### Necessity\n${a.necessityJustification || 'Not provided'}\n\n`;
    md += `### Proportionality\n${a.proportionalityJustification || 'Not provided'}\n\n`;

    md += `## 4. Risk Assessment\n\n`;
    if (a.risks.length > 0) {
      a.risks.forEach((r, i) => {
        md += `### Risk ${i + 1}: ${r.description}\n`;
        md += `- **Likelihood:** ${RISK_LEVEL_CONFIG[r.likelihood].label}\n`;
        md += `- **Impact:** ${RISK_LEVEL_CONFIG[r.impact].label}\n`;
        md += `- **Overall Risk:** ${RISK_LEVEL_CONFIG[r.overallRisk].label}\n`;
        md += `- **Residual Risk:** ${RISK_LEVEL_CONFIG[r.residualRisk].label}\n`;
        if (r.mitigationMeasures.length) {
          md += `- **Mitigations:**\n`;
          r.mitigationMeasures.forEach((m) => md += `  - ${m}\n`);
        }
        md += '\n';
      });
    } else {
      md += 'No risks documented.\n\n';
    }

    md += `## 5. Consultation\n\n`;
    md += `- DPO Consulted: ${a.dpoConsulted ? 'Yes' : 'No'}\n`;
    if (a.dpoOpinion) md += `  - Opinion: ${a.dpoOpinion}\n`;
    md += `- Data Subjects Consulted: ${a.dataSubjectsConsulted ? 'Yes' : 'No'}\n`;
    md += `- Supervisory Authority Consultation: ${a.supervisoryAuthorityConsultation ? 'Required' : 'Not Required'}\n\n`;

    md += `## 6. Review & Approval\n\n`;
    md += `| Role | Name | Date |\n`;
    md += `|------|------|------|\n`;
    md += `| Prepared by | ${a.preparedBy || '-'} | ${a.preparedDate || '-'} |\n`;
    md += `| Reviewed by | ${a.reviewedBy || '-'} | ${a.reviewDate || '-'} |\n`;
    md += `| Approved by | ${a.approvedBy || '-'} | ${a.approvalDate || '-'} |\n`;

    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${a.name.replace(/\s+/g, '-').toLowerCase()}.md`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Risk summary
  const riskSummary = currentAssessment
    ? {
        low: currentAssessment.risks.filter((r) => r.overallRisk === 'low').length,
        medium: currentAssessment.risks.filter((r) => r.overallRisk === 'medium').length,
        high: currentAssessment.risks.filter((r) => r.overallRisk === 'high').length,
        critical: currentAssessment.risks.filter((r) => r.overallRisk === 'critical').length,
      }
    : { low: 0, medium: 0, high: 0, critical: 0 };

  // Assessment list view
  if (!currentAssessment) {
    return (
      <>
        <ToolSEO
          toolName="PIA/DPIA Generator"
          toolSlug="pia-dpia"
          description="Generate Privacy Impact Assessments (PIA) and Data Protection Impact Assessments (DPIA) for GDPR compliance."
          category="assessment"
          isPremium={true}
        />
        <main className="min-h-screen bg-background text-foreground py-12 px-6">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold">PIA/DPIA Generator</h1>
                <p className="text-foreground-secondary mt-2">
                  Create Privacy and Data Protection Impact Assessments
                </p>
              </div>
              <button
                onClick={() => setShowNewDialog(true)}
                className="px-4 py-2 bg-accent text-accent-foreground rounded font-medium hover:bg-accent-hover transition-colors"
              >
                + New Assessment
              </button>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
                {error}
                <button onClick={() => setError(null)} className="ml-4 underline">Dismiss</button>
              </div>
            )}

            {/* Loading */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-foreground-tertiary">Loading assessments...</p>
              </div>
            ) : assessments.length === 0 ? (
              <div className="border border-dashed border-border rounded-lg p-12 text-center">
                <div className="text-4xl mb-4">📋</div>
                <h3 className="text-lg font-medium mb-2">No assessments yet</h3>
                <p className="text-foreground-tertiary mb-6">
                  Create your first Privacy Impact Assessment
                </p>
                <button
                  onClick={() => setShowNewDialog(true)}
                  className="px-6 py-3 bg-accent text-accent-foreground rounded font-medium hover:bg-accent-hover transition-colors"
                >
                  Create Assessment
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {assessments.map((assessment) => {
                  const highRisks = assessment.risks.filter(
                    (r) => r.overallRisk === 'high' || r.overallRisk === 'critical'
                  ).length;
                  return (
                    <div
                      key={assessment.id}
                      className="p-6 border border-border rounded-lg hover:border-accent/40 transition-colors cursor-pointer group"
                      onClick={() => handleOpenAssessment(assessment.id)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 bg-cream/10 text-foreground-secondary rounded text-xs">
                              {ASSESSMENT_TYPE_CONFIG[assessment.type].label}
                            </span>
                            <span
                              className={`px-2 py-0.5 rounded text-xs ${
                                assessment.status === 'approved'
                                  ? 'bg-green-500/20 text-green-400'
                                  : assessment.status === 'in_review'
                                  ? 'bg-blue-500/20 text-blue-400'
                                  : assessment.status === 'rejected'
                                  ? 'bg-red-500/20 text-red-400'
                                  : 'bg-yellow-500/20 text-yellow-400'
                              }`}
                            >
                              {assessment.status.replace('_', ' ')}
                            </span>
                          </div>
                          <h3 className="font-semibold text-foreground group-hover:text-accent transition-colors mt-2">
                            {assessment.name}
                          </h3>
                          {assessment.projectDescription && (
                            <p className="text-sm text-foreground-tertiary mt-1 line-clamp-2">
                              {assessment.projectDescription}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-foreground-muted">
                            {assessment.risks.length} risks
                          </div>
                          {highRisks > 0 && (
                            <div className="text-sm text-red-400">
                              {highRisks} high/critical
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-foreground-muted">
                        <span>Updated {new Date(assessment.updated_at).toLocaleDateString()}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAssessment(assessment.id);
                          }}
                          className="text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* New Assessment Dialog */}
            {showNewDialog && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-background border border-border rounded-lg max-w-md w-full p-6">
                  <h3 className="text-xl font-semibold mb-4">New Assessment</h3>
                  <div className="space-y-3 mb-6">
                    {(Object.keys(ASSESSMENT_TYPE_CONFIG) as AssessmentType[]).map((type) => {
                      const config = ASSESSMENT_TYPE_CONFIG[type];
                      return (
                        <button
                          key={type}
                          onClick={() => setSelectedType(type)}
                          className={`w-full p-4 text-left border rounded-lg transition-colors ${
                            selectedType === type
                              ? 'border-accent bg-accent-muted'
                              : 'border-border hover:border-border/40'
                          }`}
                        >
                          <div className="font-medium">
                            {config.label} - {config.fullName}
                          </div>
                          <div className="text-sm text-foreground-tertiary mt-1">{config.description}</div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowNewDialog(false)}
                      className="px-4 py-2 text-foreground-secondary hover:text-foreground"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateAssessment}
                      className="px-4 py-2 bg-accent text-accent-foreground rounded font-medium"
                    >
                      Create
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </>
    );
  }

  // Assessment editor view
  const sections: Array<{ key: EditorSection; label: string; icon: string }> = [
    { key: 'overview', label: 'Overview', icon: '📋' },
    { key: 'data_flows', label: 'Data Flows', icon: '🔄' },
    { key: 'necessity', label: 'Necessity', icon: '⚖️' },
    { key: 'risks', label: 'Risks', icon: '⚠️' },
    { key: 'consultation', label: 'Consultation', icon: '💬' },
    { key: 'review', label: 'Review', icon: '✅' },
  ];

  return (
    <>
      <ToolSEO
        toolName="PIA/DPIA Generator"
        toolSlug="pia-dpia"
        description="Generate Privacy Impact Assessments (PIA) and Data Protection Impact Assessments (DPIA) for GDPR compliance."
        category="assessment"
        isPremium={true}
      />
      <main className="min-h-screen bg-background text-foreground py-8 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setCurrentAssessment(null)}
                className="text-foreground-tertiary hover:text-foreground transition-colors"
              >
                ← Back
              </button>
              <div>
                <input
                  type="text"
                  value={currentAssessment.name}
                  onChange={(e) => updateAssessment({ name: e.target.value })}
                  className="text-2xl font-bold bg-transparent border-none text-foreground focus:outline-none"
                />
                <div className="flex items-center gap-2 text-sm text-foreground-tertiary">
                  <span className="px-2 py-0.5 bg-cream/10 rounded">
                    {ASSESSMENT_TYPE_CONFIG[currentAssessment.type].label}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded ${
                      currentAssessment.status === 'approved'
                        ? 'bg-green-500/20 text-green-400'
                        : currentAssessment.status === 'in_review'
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}
                  >
                    {currentAssessment.status.replace('_', ' ')}
                  </span>
                  {saving && <span className="text-accent">Saving...</span>}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={currentAssessment.status}
                onChange={(e) => updateAssessment({ status: e.target.value as Assessment['status'] })}
                className="px-3 py-2 bg-cream/10 border border-border rounded text-foreground"
              >
                <option value="draft">Draft</option>
                <option value="in_review">In Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <button
                onClick={handleExportMarkdown}
                className="px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 transition-colors"
              >
                Export
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
              {error}
              <button onClick={() => setError(null)} className="ml-4 underline">Dismiss</button>
            </div>
          )}

          {/* Risk summary bar */}
          {currentAssessment.risks.length > 0 && (
            <div className="grid grid-cols-4 gap-4 mb-6">
              {(['low', 'medium', 'high', 'critical'] as RiskLevel[]).map((level) => (
                <div
                  key={level}
                  className="p-3 border border-border-muted rounded-lg text-center"
                  style={{ borderColor: riskSummary[level] > 0 ? RISK_LEVEL_CONFIG[level].color : undefined }}
                >
                  <div className="text-xl font-bold" style={{ color: RISK_LEVEL_CONFIG[level].color }}>
                    {riskSummary[level]}
                  </div>
                  <div className="text-xs text-foreground-tertiary">{RISK_LEVEL_CONFIG[level].label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Section tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {sections.map((section) => (
              <button
                key={section.key}
                onClick={() => setActiveSection(section.key)}
                className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  activeSection === section.key
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-cream/10 text-foreground hover:bg-cream/20'
                }`}
              >
                {section.icon} {section.label}
              </button>
            ))}
          </div>

          {/* Section content */}
          <div className="border border-border rounded-lg p-6">
            {activeSection === 'overview' && (
              <OverviewSection assessment={currentAssessment} onUpdate={updateAssessment} />
            )}
            {activeSection === 'data_flows' && (
              <DataFlowsSection assessment={currentAssessment} onUpdate={updateAssessment} />
            )}
            {activeSection === 'necessity' && (
              <NecessitySection assessment={currentAssessment} onUpdate={updateAssessment} />
            )}
            {activeSection === 'risks' && (
              <RisksSection assessment={currentAssessment} onUpdate={updateAssessment} />
            )}
            {activeSection === 'consultation' && (
              <ConsultationSection assessment={currentAssessment} onUpdate={updateAssessment} />
            )}
            {activeSection === 'review' && (
              <ReviewSection assessment={currentAssessment} onUpdate={updateAssessment} />
            )}
          </div>
        </div>
      </main>
    </>
  );
}

// Section Components
function OverviewSection({ assessment, onUpdate }: { assessment: Assessment; onUpdate: (u: Partial<Assessment>) => void }) {
  const [purposeText, setPurposeText] = useState('');
  const [purposeBasis, setPurposeBasis] = useState<ProcessingLawfulBasis>('legitimate_interests');

  const addPurpose = () => {
    if (!purposeText.trim()) return;
    const newPurpose: ProcessingPurpose = {
      id: generatePurposeId(),
      description: purposeText.trim(),
      lawfulBasis: purposeBasis,
    };
    onUpdate({ purposes: [...assessment.purposes, newPurpose] });
    setPurposeText('');
  };

  const removePurpose = (id: string) => {
    onUpdate({ purposes: assessment.purposes.filter((p) => p.id !== id) });
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1">Project Description</label>
        <textarea
          value={assessment.projectDescription}
          onChange={(e) => onUpdate({ projectDescription: e.target.value })}
          placeholder="Describe the project or initiative being assessed..."
          rows={3}
          className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1">Processing Description</label>
        <textarea
          value={assessment.processingDescription}
          onChange={(e) => onUpdate({ processingDescription: e.target.value })}
          placeholder="Describe how personal data will be collected, used, stored, and shared..."
          rows={3}
          className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground"
        />
      </div>

      {/* Purposes */}
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-2">Processing Purposes</label>
        {assessment.purposes.map((p) => (
          <div key={p.id} className="flex items-start gap-2 mb-2 p-3 bg-cream/5 rounded-lg">
            <div className="flex-1">
              <div className="text-foreground">{p.description}</div>
              <div className="text-xs text-foreground-tertiary">
                {LAWFUL_BASIS_CONFIG[p.lawfulBasis].label} ({LAWFUL_BASIS_CONFIG[p.lawfulBasis].gdprArticle})
              </div>
            </div>
            <button onClick={() => removePurpose(p.id)} className="text-red-400 hover:text-red-300">×</button>
          </div>
        ))}
        <div className="flex gap-2">
          <input
            type="text"
            value={purposeText}
            onChange={(e) => setPurposeText(e.target.value)}
            placeholder="Add processing purpose..."
            className="flex-1 px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground text-sm"
          />
          <select
            value={purposeBasis}
            onChange={(e) => setPurposeBasis(e.target.value as ProcessingLawfulBasis)}
            className="px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground text-sm"
          >
            {(Object.keys(LAWFUL_BASIS_CONFIG) as ProcessingLawfulBasis[]).map((basis) => (
              <option key={basis} value={basis}>{LAWFUL_BASIS_CONFIG[basis].label}</option>
            ))}
          </select>
          <button onClick={addPurpose} className="px-4 py-2 bg-accent text-accent-foreground rounded font-medium text-sm">
            Add
          </button>
        </div>
      </div>

      {/* Data Subjects */}
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-2">Data Subjects</label>
        <div className="grid grid-cols-3 gap-2">
          {(Object.keys(DATA_SUBJECT_CONFIG) as DataSubjectCategory[]).map((cat) => (
            <label key={cat} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={assessment.dataSubjects.includes(cat)}
                onChange={(e) => {
                  if (e.target.checked) {
                    onUpdate({ dataSubjects: [...assessment.dataSubjects, cat] });
                  } else {
                    onUpdate({ dataSubjects: assessment.dataSubjects.filter((c) => c !== cat) });
                  }
                }}
                className="rounded border-border"
              />
              <span className="text-sm text-foreground-secondary">{DATA_SUBJECT_CONFIG[cat].label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Personal Data Types */}
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-2">Personal Data Types</label>
        <div className="grid grid-cols-3 gap-2">
          {(Object.keys(PERSONAL_DATA_TYPE_CONFIG) as PersonalDataType[]).map((type) => {
            const config = PERSONAL_DATA_TYPE_CONFIG[type];
            return (
              <label key={type} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={assessment.personalDataTypes.includes(type)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onUpdate({ personalDataTypes: [...assessment.personalDataTypes, type] });
                    } else {
                      onUpdate({ personalDataTypes: assessment.personalDataTypes.filter((t) => t !== type) });
                    }
                  }}
                  className="rounded border-border"
                />
                <span className={`text-sm ${config.isSpecialCategory ? 'text-red-400' : 'text-foreground-secondary'}`}>
                  {config.label} {config.isSpecialCategory && '⚠️'}
                </span>
              </label>
            );
          })}
        </div>
        <p className="text-xs text-foreground-muted mt-2">⚠️ indicates special category data under GDPR Article 9</p>
      </div>
    </div>
  );
}

function DataFlowsSection({ assessment, onUpdate }: { assessment: Assessment; onUpdate: (u: Partial<Assessment>) => void }) {
  const [processorInput, setProcessorInput] = useState('');

  const addDataFlow = () => {
    const newFlow: DataFlow = {
      id: generateDataFlowId(),
      source: '',
      destination: '',
      dataTypes: [],
    };
    onUpdate({ dataFlows: [...assessment.dataFlows, newFlow] });
  };

  const updateDataFlow = (id: string, updates: Partial<DataFlow>) => {
    onUpdate({
      dataFlows: assessment.dataFlows.map((f) => (f.id === id ? { ...f, ...updates } : f)),
    });
  };

  const removeDataFlow = (id: string) => {
    onUpdate({ dataFlows: assessment.dataFlows.filter((f) => f.id !== id) });
  };

  const addProcessor = () => {
    if (!processorInput.trim()) return;
    onUpdate({ thirdPartyProcessors: [...assessment.thirdPartyProcessors, processorInput.trim()] });
    setProcessorInput('');
  };

  return (
    <div className="space-y-6">
      {/* Data Flows */}
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-2">Data Flows</label>
        {assessment.dataFlows.map((flow) => (
          <div key={flow.id} className="p-4 border border-border-muted rounded-lg mb-3">
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Source</label>
                <input
                  type="text"
                  value={flow.source}
                  onChange={(e) => updateDataFlow(flow.id, { source: e.target.value })}
                  placeholder="e.g., Web Form"
                  className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Destination</label>
                <input
                  type="text"
                  value={flow.destination}
                  onChange={(e) => updateDataFlow(flow.id, { destination: e.target.value })}
                  placeholder="e.g., CRM Database"
                  className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-foreground-tertiary mb-1">Safeguards</label>
              <input
                type="text"
                value={flow.safeguards || ''}
                onChange={(e) => updateDataFlow(flow.id, { safeguards: e.target.value })}
                placeholder="e.g., TLS encryption, access controls"
                className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground text-sm"
              />
            </div>
            <button
              onClick={() => removeDataFlow(flow.id)}
              className="mt-2 text-sm text-red-400 hover:text-red-300"
            >
              Remove
            </button>
          </div>
        ))}
        <button onClick={addDataFlow} className="text-sm text-accent hover:underline">
          + Add Data Flow
        </button>
      </div>

      {/* Third Party Processors */}
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-2">Third Party Processors</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {assessment.thirdPartyProcessors.map((proc, idx) => (
            <span key={idx} className="px-2 py-1 bg-cream/10 rounded text-sm flex items-center gap-1">
              {proc}
              <button
                onClick={() => onUpdate({ thirdPartyProcessors: assessment.thirdPartyProcessors.filter((_, i) => i !== idx) })}
                className="text-foreground-tertiary hover:text-foreground"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={processorInput}
            onChange={(e) => setProcessorInput(e.target.value)}
            placeholder="Add processor name..."
            className="flex-1 px-3 py-2 bg-background/60 border border-border rounded text-foreground text-sm"
          />
          <button onClick={addProcessor} className="px-4 py-2 bg-accent text-accent-foreground rounded font-medium text-sm">
            Add
          </button>
        </div>
      </div>

      {/* International Transfers */}
      <div>
        <label className="flex items-center gap-2 cursor-pointer mb-2">
          <input
            type="checkbox"
            checked={assessment.internationalTransfers}
            onChange={(e) => onUpdate({ internationalTransfers: e.target.checked })}
            className="rounded border-border"
          />
          <span className="text-sm text-foreground-secondary">International data transfers</span>
        </label>
        {assessment.internationalTransfers && (
          <input
            type="text"
            value={assessment.transferCountries?.join(', ') || ''}
            onChange={(e) => onUpdate({ transferCountries: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
            placeholder="Countries (comma-separated)..."
            className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground text-sm"
          />
        )}
      </div>

      {/* Data Retention */}
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1">Data Retention Period</label>
        <input
          type="text"
          value={assessment.dataRetention || ''}
          onChange={(e) => onUpdate({ dataRetention: e.target.value })}
          placeholder="e.g., 7 years after contract end"
          className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground"
        />
      </div>
    </div>
  );
}

function NecessitySection({ assessment, onUpdate }: { assessment: Assessment; onUpdate: (u: Partial<Assessment>) => void }) {
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1">Necessity Justification</label>
        <p className="text-xs text-foreground-tertiary mb-2">
          Explain why the processing is necessary for the stated purposes. Why can&apos;t the purpose be achieved without this processing?
        </p>
        <textarea
          value={assessment.necessityJustification}
          onChange={(e) => onUpdate({ necessityJustification: e.target.value })}
          placeholder="The processing is necessary because..."
          rows={4}
          className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1">Proportionality Justification</label>
        <p className="text-xs text-foreground-tertiary mb-2">
          Explain how the processing is proportionate to the purpose. Is the amount of data collected the minimum necessary?
        </p>
        <textarea
          value={assessment.proportionalityJustification}
          onChange={(e) => onUpdate({ proportionalityJustification: e.target.value })}
          placeholder="The processing is proportionate because..."
          rows={4}
          className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1">Alternatives Considered</label>
        <p className="text-xs text-foreground-tertiary mb-2">
          What alternatives were considered and why were they rejected?
        </p>
        <textarea
          value={assessment.alternativesConsidered || ''}
          onChange={(e) => onUpdate({ alternativesConsidered: e.target.value })}
          placeholder="We considered the following alternatives..."
          rows={3}
          className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1">Data Minimization Measures</label>
        <textarea
          value={assessment.dataMinimization || ''}
          onChange={(e) => onUpdate({ dataMinimization: e.target.value })}
          placeholder="Describe how you ensure only necessary data is collected..."
          rows={3}
          className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground"
        />
      </div>
    </div>
  );
}

function RisksSection({ assessment, onUpdate }: { assessment: Assessment; onUpdate: (u: Partial<Assessment>) => void }) {
  const addRisk = () => {
    const newRisk: IdentifiedRisk = {
      id: generateRiskId(),
      description: '',
      likelihood: 'medium',
      impact: 'medium',
      overallRisk: 'medium',
      affectedRights: [],
      mitigationMeasures: [],
      residualRisk: 'low',
    };
    onUpdate({ risks: [...assessment.risks, newRisk] });
  };

  const updateRisk = (id: string, updates: Partial<IdentifiedRisk>) => {
    onUpdate({
      risks: assessment.risks.map((r) => {
        if (r.id !== id) return r;
        const updated = { ...r, ...updates };
        // Recalculate overall risk if likelihood or impact changed
        if (updates.likelihood || updates.impact) {
          updated.overallRisk = calculateOverallRisk(
            updates.likelihood || r.likelihood,
            updates.impact || r.impact
          );
        }
        return updated;
      }),
    });
  };

  const removeRisk = (id: string) => {
    onUpdate({ risks: assessment.risks.filter((r) => r.id !== id) });
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-foreground-tertiary">
        Identify risks to data subjects&apos; rights and freedoms, assess their likelihood and impact, and document mitigation measures.
      </p>

      {assessment.risks.map((risk, idx) => (
        <div key={risk.id} className="p-4 border border-border-muted rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <span className="font-medium text-foreground">Risk {idx + 1}</span>
            <div className="flex items-center gap-2">
              <span
                className="px-2 py-1 rounded text-xs font-medium"
                style={{
                  backgroundColor: `${RISK_LEVEL_CONFIG[risk.overallRisk].color}20`,
                  color: RISK_LEVEL_CONFIG[risk.overallRisk].color,
                }}
              >
                {RISK_LEVEL_CONFIG[risk.overallRisk].label} Risk
              </span>
              <button onClick={() => removeRisk(risk.id)} className="text-red-400 hover:text-red-300 text-sm">
                Remove
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs text-foreground-tertiary mb-1">Risk Description</label>
              <textarea
                value={risk.description}
                onChange={(e) => updateRisk(risk.id, { description: e.target.value })}
                placeholder="Describe the risk..."
                rows={2}
                className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground text-sm"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Likelihood</label>
                <select
                  value={risk.likelihood}
                  onChange={(e) => updateRisk(risk.id, { likelihood: e.target.value as RiskLevel })}
                  className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground text-sm"
                >
                  {(Object.keys(RISK_LEVEL_CONFIG) as RiskLevel[]).map((level) => (
                    <option key={level} value={level}>{RISK_LEVEL_CONFIG[level].label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Impact</label>
                <select
                  value={risk.impact}
                  onChange={(e) => updateRisk(risk.id, { impact: e.target.value as RiskLevel })}
                  className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground text-sm"
                >
                  {(Object.keys(RISK_LEVEL_CONFIG) as RiskLevel[]).map((level) => (
                    <option key={level} value={level}>{RISK_LEVEL_CONFIG[level].label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Residual Risk</label>
                <select
                  value={risk.residualRisk}
                  onChange={(e) => updateRisk(risk.id, { residualRisk: e.target.value as RiskLevel })}
                  className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground text-sm"
                >
                  {(Object.keys(RISK_LEVEL_CONFIG) as RiskLevel[]).map((level) => (
                    <option key={level} value={level}>{RISK_LEVEL_CONFIG[level].label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs text-foreground-tertiary mb-1">Mitigation Measures</label>
              <textarea
                value={risk.mitigationMeasures.join('\n')}
                onChange={(e) => updateRisk(risk.id, { mitigationMeasures: e.target.value.split('\n').filter(Boolean) })}
                placeholder="One mitigation per line..."
                rows={3}
                className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground text-sm"
              />
            </div>
          </div>
        </div>
      ))}

      <button onClick={addRisk} className="text-sm text-accent hover:underline">
        + Add Risk
      </button>
    </div>
  );
}

function ConsultationSection({ assessment, onUpdate }: { assessment: Assessment; onUpdate: (u: Partial<Assessment>) => void }) {
  return (
    <div className="space-y-6">
      {/* DPO Consultation */}
      <div className="p-4 border border-border-muted rounded-lg">
        <label className="flex items-center gap-2 cursor-pointer mb-3">
          <input
            type="checkbox"
            checked={assessment.dpoConsulted}
            onChange={(e) => onUpdate({ dpoConsulted: e.target.checked })}
            className="rounded border-border"
          />
          <span className="font-medium text-foreground">Data Protection Officer Consulted</span>
        </label>
        {assessment.dpoConsulted && (
          <textarea
            value={assessment.dpoOpinion || ''}
            onChange={(e) => onUpdate({ dpoOpinion: e.target.value })}
            placeholder="DPO's opinion and recommendations..."
            rows={3}
            className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground text-sm"
          />
        )}
      </div>

      {/* Data Subject Consultation */}
      <div className="p-4 border border-border-muted rounded-lg">
        <label className="flex items-center gap-2 cursor-pointer mb-3">
          <input
            type="checkbox"
            checked={assessment.dataSubjectsConsulted}
            onChange={(e) => onUpdate({ dataSubjectsConsulted: e.target.checked })}
            className="rounded border-border"
          />
          <span className="font-medium text-foreground">Data Subjects Consulted</span>
        </label>
        {assessment.dataSubjectsConsulted && (
          <textarea
            value={assessment.consultationDetails || ''}
            onChange={(e) => onUpdate({ consultationDetails: e.target.value })}
            placeholder="Details of consultation with data subjects..."
            rows={3}
            className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground text-sm"
          />
        )}
      </div>

      {/* Supervisory Authority */}
      <div className="p-4 border border-border-muted rounded-lg">
        <label className="flex items-center gap-2 cursor-pointer mb-3">
          <input
            type="checkbox"
            checked={assessment.supervisoryAuthorityConsultation}
            onChange={(e) => onUpdate({ supervisoryAuthorityConsultation: e.target.checked })}
            className="rounded border-border"
          />
          <span className="font-medium text-foreground">Supervisory Authority Consultation Required</span>
        </label>
        <p className="text-xs text-foreground-tertiary mb-2">
          Under GDPR Article 36, prior consultation with the supervisory authority is required if high risks cannot be mitigated.
        </p>
        {assessment.supervisoryAuthorityConsultation && (
          <textarea
            value={assessment.authorityDetails || ''}
            onChange={(e) => onUpdate({ authorityDetails: e.target.value })}
            placeholder="Details of supervisory authority consultation..."
            rows={3}
            className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground text-sm"
          />
        )}
      </div>
    </div>
  );
}

function ReviewSection({ assessment, onUpdate }: { assessment: Assessment; onUpdate: (u: Partial<Assessment>) => void }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div>
          <h4 className="font-medium text-foreground mb-3">Prepared By</h4>
          <div className="space-y-3">
            <input
              type="text"
              value={assessment.preparedBy}
              onChange={(e) => onUpdate({ preparedBy: e.target.value })}
              placeholder="Name"
              className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground"
            />
            <input
              type="date"
              value={assessment.preparedDate}
              onChange={(e) => onUpdate({ preparedDate: e.target.value })}
              className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground"
            />
          </div>
        </div>

        <div>
          <h4 className="font-medium text-foreground mb-3">Reviewed By</h4>
          <div className="space-y-3">
            <input
              type="text"
              value={assessment.reviewedBy || ''}
              onChange={(e) => onUpdate({ reviewedBy: e.target.value })}
              placeholder="Name"
              className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground"
            />
            <input
              type="date"
              value={assessment.reviewDate || ''}
              onChange={(e) => onUpdate({ reviewDate: e.target.value })}
              className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <h4 className="font-medium text-foreground mb-3">Approved By</h4>
          <div className="space-y-3">
            <input
              type="text"
              value={assessment.approvedBy || ''}
              onChange={(e) => onUpdate({ approvedBy: e.target.value })}
              placeholder="Name"
              className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground"
            />
            <input
              type="date"
              value={assessment.approvalDate || ''}
              onChange={(e) => onUpdate({ approvalDate: e.target.value })}
              className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground"
            />
          </div>
        </div>

        <div>
          <h4 className="font-medium text-foreground mb-3">Next Review Date</h4>
          <input
            type="date"
            value={assessment.nextReviewDate || ''}
            onChange={(e) => onUpdate({ nextReviewDate: e.target.value })}
            className="w-full px-3 py-2 bg-background/60 border border-border rounded text-foreground"
          />
          <p className="text-xs text-foreground-tertiary mt-2">
            DPIAs should be reviewed regularly, especially when processing changes.
          </p>
        </div>
      </div>
    </div>
  );
}
